import { CsvBulkConfig, CsvFieldDefinition } from 'components/csv/types';
import { ClientResult } from 'stores/ClientStoreAdapter';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import {
  CreateProjectParams,
  ManagerResult,
  TechStackItem,
  createProject,
} from 'stores/ProjectStoreAdapter';

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/**
 * プロジェクトCSV一括登録用のフィールド定義を生成する
 */
export const createProjectCsvFields = (
  managers: ManagerResult[],
  clients: ClientResult[],
  projectScales: MasterTypeResult[],
  distributions: MasterTypeResult[],
  industries: MasterTypeResult[],
  techStackItems: TechStackItem[],
): CsvFieldDefinition[] => [
  {
    header: 'プロジェクト名',
    key: 'projectName',
    description: 'プロジェクト名（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: 'プロジェクト名（カナ）',
    key: 'projectNameKana',
    description: 'プロジェクト名カナ（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '課長ID',
    key: 'managerId',
    description: 'MANAGERロールのユーザーID（必須）',
    required: true,
    type: 'id',
    options: managers.map((m) => ({ code: m.userId, label: m.userName })),
  },
  {
    header: '取引先ID',
    key: 'clientId',
    description: '取引先ID（必須）',
    required: true,
    type: 'id',
    options: clients.map((c) => ({ code: c.clientId, label: c.clientName })),
  },
  {
    header: 'プロジェクト規模ID',
    key: 'projectScaleId',
    description: 'プロジェクト規模のコード値（任意）',
    required: false,
    type: 'select',
    options: projectScales.map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: '商流ID',
    key: 'distributionId',
    description: '商流のコード値（任意）',
    required: false,
    type: 'select',
    options: distributions.map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: '業種ID',
    key: 'industryIds',
    description: '業種コード値（任意、カンマ区切りで複数指定可）',
    required: false,
    type: 'multi-select',
    separator: ',',
    options: industries.map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: '技術スタックID',
    key: 'techStackIds',
    description: '技術スタックID（任意、カンマ区切りで複数指定可）',
    required: false,
    type: 'multi-select',
    separator: ',',
    options: techStackItems.map((t) => ({ code: t.techStackId, label: t.techStackName })),
  },
  {
    header: '開始日',
    key: 'startDate',
    description: "開始日（任意、format: yyyy-MM-dd'T'HH:mm:ss）",
    required: false,
    type: 'text',
  },
  {
    header: '備考',
    key: 'remarks',
    description: '備考（任意、最大10000文字）',
    required: false,
    type: 'text',
    maxLength: 10000,
  },
];

/**
 * プロジェクトCSV一括登録のConfig生成関数
 */
export const createProjectBulkConfig = (
  managers: ManagerResult[],
  clients: ClientResult[],
  projectScales: MasterTypeResult[],
  distributions: MasterTypeResult[],
  industries: MasterTypeResult[],
  techStackItems: TechStackItem[],
): CsvBulkConfig<CreateProjectParams> => {
  const fields = createProjectCsvFields(
    managers,
    clients,
    projectScales,
    distributions,
    industries,
    techStackItems,
  );

  return {
    entityName: 'プロジェクト',
    fields,
    toApiParams: (row: Record<string, string>): CreateProjectParams => {
      const params: CreateProjectParams = {
        projectName: row.projectName,
        projectNameKana: row.projectNameKana,
        managerId: row.managerId,
        clientId: row.clientId,
      };

      if (row.projectScaleId) params.projectScaleId = row.projectScaleId;
      if (row.distributionId) params.distributionId = row.distributionId;
      if (row.industryIds) {
        params.industryIds = row.industryIds
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }
      if (row.techStackIds) {
        params.techStackIds = row.techStackIds
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }
      if (row.startDate) params.startDate = row.startDate;
      if (row.remarks) params.remarks = row.remarks;

      return params;
    },
    registerOne: createProject,
    resolveDisplayValue: (key: string, rawValue: string): string | null => {
      if (!rawValue) return null;

      const field = fields.find((f) => f.key === key);
      if (!field || !field.options) return null;

      if (field.type === 'multi-select') {
        const separator = field.separator || ',';
        const values = rawValue.split(separator).map((v) => v.trim());
        const labels = values
          .map((v) => {
            const option = field.options?.find((o) => o.code === v);
            return option ? option.label : null;
          })
          .filter(Boolean);
        return labels.length > 0 ? labels.join(', ') : null;
      }

      const option = field.options.find((o) => o.code === rawValue);
      return option ? option.label : null;
    },
    validateField: (key: string, value: string, _row: Record<string, string>): string | null => {
      // 課長IDの存在チェック
      if (key === 'managerId' && value) {
        const managerExists = managers.some((m) => m.userId === value);
        if (!managerExists) return `課長ID「${value}」は存在しません`;
      }

      // 取引先IDの存在チェック
      if (key === 'clientId' && value) {
        const clientExists = clients.some((c) => c.clientId === value);
        if (!clientExists) return `取引先ID「${value}」は存在しません`;
      }

      // 開始日のフォーマットチェック
      if (key === 'startDate' && value && !DATETIME_REGEX.test(value)) {
        return "開始日はyyyy-MM-dd'T'HH:mm:ss形式で入力してください";
      }

      return null;
    },
  };
};
